import { Injectable } from '@angular/core';
import type { PostHog } from 'posthog-js';
import { environment } from 'src/environments/environment';

/**
 * Module-level (not per-instance) state: posthog-js must only be loaded and
 * init()'d once for the whole app, regardless of how many places call in
 * (AnalyticsService injections, or the eager preload() from main.ts). Keeping
 * this at module scope — rather than on the Angular service instance — is what
 * makes that guarantee hold, since a plain function call (from main.ts) and a
 * DI'd instance (from components) both resolve to the same JS module.
 */
/**
 * Who is looking at an event.
 *  - `host`            — owns the event
 *  - `guest`           — signed in, but not the owner
 *  - `anonymous_guest` — arrived by link/QR without an account
 * The last two are separated because they convert very differently.
 */
export type AnalyticsRole = 'host' | 'guest' | 'anonymous_guest';

let ph: PostHog | null = null;
let loading: Promise<PostHog> | null = null;
let queue: Array<(ph: PostHog) => void> = [];

const phEnvironment =
  window.location.hostname === 'events.picaggo.com' ? 'production' :
  (window.location.hostname === 'test-picaggo.web.app' || window.location.hostname === 'mypicaggo.web.app') ? 'sandbox' :
  'development';

/** Run an action against posthog now if ready, otherwise queue it and trigger load+init. */
function run(action: (ph: PostHog) => void): void {
  if (ph) {
    action(ph);
    return;
  }
  queue.push(action);
  void ensureLoaded();
}

function ensureLoaded(): Promise<PostHog> {
  if (loading) {
    return loading;
  }
  loading = import('posthog-js').then(({ default: posthog }) => {
    posthog.init(environment.posthogKey, {
      api_host: 'https://us.i.posthog.com',
      // Cookie as well, scoped to .picaggo.com to match ph.js on the website,
      // so a visit to picaggo.com and the signup here are one person.
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      autocapture: true,
      // Off deliberately. The app is hash-routed (`useHash: true`), so the route
      // lives after the `#` and posthog-js records every automatic pageview as
      // `$pathname = '/'`. AppComponent captures $pageview on NavigationEnd
      // instead, sending the resolved route pattern explicitly.
      capture_pageview: false,
      disable_session_recording: phEnvironment !== 'production',
    });
    // Register unconditionally right after init — NOT inside posthog-js's
    // `loaded` callback. `loaded` waits on the remote-config (decide) request,
    // which a blocked/slow network (ad blockers routinely block this) can
    // delay or never fire; register() itself is a synchronous, local
    // (localStorage-backed) call with no reason to wait on that round-trip.
    posthog.register({ environment: phEnvironment, platform: 'pwa' });
    if (phEnvironment !== 'production') {
      posthog.debug(false);
    }
    ph = posthog;
    // Flush anything queued before init finished.
    const pending = queue;
    queue = [];
    pending.forEach(fn => fn(posthog));
    return posthog;
  });
  return loading;
}

/** Kick off the load+init eagerly (e.g. from main.ts, off the critical path), without queuing any action. */
export function preloadAnalytics(): void {
  void ensureLoaded();
}

/**
 * Run something against posthog once it is ready, queueing if it is not.
 *
 * Exists so main.ts can cross-link PostHog and Sentry without importing
 * posthog-js directly, which would pull its ~305KB back into the initial
 * bundle — the whole reason this service lazy-loads.
 */
export function onAnalyticsReady(action: (ph: PostHog) => void): void {
  run(action);
}

/**
 * Record that Sentry captured an error, with the link back to it.
 *
 * Deliberately *not* posthog's own exception autocapture: Sentry already owns
 * error capture, grouping and stack traces, and running both would mean the
 * same errors in two places under two sets of filtering rules. This only
 * mirrors the fact an error happened so that a drop-off in PostHog can be
 * asked "did this person hit an error?" — and answered with a link.
 *
 * `guard` prevents a loop: if capturing itself throws, Sentry would catch that
 * and call back in here forever.
 */
let linkingSentryEvent = false;
export function captureSentryLink(props: Record<string, any>): void {
  if (linkingSentryEvent) return;
  linkingSentryEvent = true;
  try {
    run(p => p.capture('$exception', props));
  } finally {
    linkingSentryEvent = false;
  }
}

/**
 * Thin wrapper around posthog-js that lazy-loads the library on first use, so
 * its ~305KB stays out of the initial bundle. Components/services should call
 * this service instead of importing `posthog-js` directly.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  capture(event: string, properties?: Record<string, any>): void {
    run(p => p.capture(event, properties));
  }

  identify(distinctId: string, properties?: Record<string, any>): void {
    run(p => p.identify(distinctId, properties));
  }

  reset(): void {
    run(p => p.reset());
  }

  /**
   * Whether a PostHog feature flag is currently ON for this visitor.
   *
   * Deliberately fail-open: if posthog-js hasn't loaded, the flags request
   * hasn't come back, or an ad blocker killed it outright, this returns false.
   * That makes it safe for kill switches (`flag ON = feature OFF`) and unsafe
   * for enable switches — a blocked PostHog must never be able to silently
   * disable a shipped feature, only a deliberate toggle should.
   */
  isFlagOn(flag: string): boolean {
    if (!flag || !ph) return false;
    try {
      return ph.isFeatureEnabled(flag) === true;
    } catch {
      return false;
    }
  }

  /**
   * Stamp every subsequent event with the event it belongs to and the viewer's
   * role, using posthog's registered (super) properties.
   *
   * This exists so `event_id` and `role` are never added per call site — that
   * approach left `role` on 1 of 96 `event_created` and `event_id` on none of
   * the invite events, which is what makes "this host shared, did guests
   * arrive?" unanswerable today.
   *
   * `event_id` is always the numeric database id, never the share hex. Both
   * used to appear under the same key, so joins silently returned nothing.
   * The hex still travels, under `event_hex`, for debugging.
   */
  setEventContext(eventId: number | string | null | undefined, role: AnalyticsRole, eventHex?: string | null): void {
    const numeric = Number(eventId);
    const props: Record<string, any> = { role };
    if (Number.isFinite(numeric) && numeric > 0) {
      props['event_id'] = numeric;
    }
    if (eventHex) {
      props['event_hex'] = eventHex;
    }
    run(p => p.register(props));
  }

  /**
   * Drop the event context on leaving an event. Without this a later unrelated
   * event — a pricing page view, say — would still claim to belong to whatever
   * event was open last, which is worse than having no context at all.
   */
  clearEventContext(): void {
    run(p => {
      p.unregister('event_id');
      p.unregister('event_hex');
      p.unregister('role');
    });
  }
}
